import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { GraduationCap, Search, Eye, Play, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { VideoItem } from '@/types/educaliec';
import { educaliecCategories } from '@/data/educaliec-categories';
import { ErrorState } from './ErrorState';

interface EducaLIECContentProps {
  videos: VideoItem[];
  loading: boolean;
  error: string | null;
  onRetry?: () => void;
}

export function EducaLIECContent({ videos, loading, error, onRetry }: EducaLIECContentProps) {
  const [selectedVideo, setSelectedVideo] = useState<VideoItem | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [activeCategory, setActiveCategory] = useState('todos');
  const [searchQuery, setSearchQuery] = useState('');

  // Reset playing state when selected video changes
  useEffect(() => {
    setIsPlaying(false);
  }, [selectedVideo?.id]);

  if (error) {
    return (
      <div className="min-h-[calc(100vh-80px)] flex items-center justify-center">
        <ErrorState message={error} onRetry={onRetry} />
      </div>
    );
  }

  // Filter videos
  const filteredVideos = videos.filter((video) => {
    if (activeCategory !== 'todos' && !video.categoryIds.includes(activeCategory)) {
      return false;
    }
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      if (!video.title.toLowerCase().includes(query) && !video.description.toLowerCase().includes(query)) {
        return false;
      }
    }
    return true;
  });

  // Videos for the sidebar (exclude selected)
  const sidebarVideos = selectedVideo
    ? filteredVideos.filter((v) => v.id !== selectedVideo.id)
    : filteredVideos;

  return (
    <div className="min-h-[calc(100vh-80px)]">
      {/* Header Section */}
      <div className="bg-[#104D8D] text-white py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <GraduationCap className="w-8 h-8 opacity-90" />
            <h1 className="text-2xl md:text-3xl font-bold tracking-wide">EducaLIEC</h1>
          </div>
          <p className="text-white/80 text-sm md:text-base max-w-xl">
            Biblioteca de capacitación técnica en materiales, normatividad y ensayos de laboratorio.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-gray-50 py-4 px-4">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row gap-3">
          {/* Category Filter */}
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide flex-1">
            {educaliecCategories.map((category) => {
              const isActive = category.id === activeCategory;
              return (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={cn(
                    'px-4 py-1.5 text-sm font-medium whitespace-nowrap transition-colors duration-200',
                    'focus:outline-none focus:ring-2 focus:ring-liec-primary-500 focus:ring-offset-1',
                    isActive
                      ? 'bg-liec-primary-500 text-white'
                      : 'bg-white text-liec-neutral-700 hover:bg-gray-100'
                  )}
                >
                  {category.label}
                </button>
              );
            })}
          </div>

          {/* Search */}
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-liec-neutral-500 pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar videos..."
              className={cn(
                'w-full pl-10 pr-4 py-2 text-sm bg-white',
                'text-liec-neutral-700 placeholder:text-liec-neutral-400',
                'focus:outline-none focus:ring-2 focus:ring-liec-primary-500',
                'transition-colors duration-200'
              )}
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {loading ? (
          <VideoGridSkeleton />
        ) : filteredVideos.length === 0 ? (
          <div className="flex items-center justify-center py-20">
            <p className="text-liec-neutral-500 text-sm">No se encontraron videos</p>
          </div>
        ) : selectedVideo ? (
          /* Selected Video Layout: Player + Sidebar */
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Main Player */}
            <motion.div
              className="flex-1"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
            >
              <div className="relative w-full aspect-video bg-black overflow-hidden">
                {isPlaying ? (
                  <iframe
                    src={`https://www.youtube.com/embed/${selectedVideo.id}?autoplay=1`}
                    title={selectedVideo.title}
                    className="absolute inset-0 w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <>
                    <img
                      src={`https://img.youtube.com/vi/${selectedVideo.id}/maxresdefault.jpg`}
                      alt={selectedVideo.title}
                      className="absolute inset-0 w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        if (target.src.includes('maxresdefault')) {
                          target.src = `https://img.youtube.com/vi/${selectedVideo.id}/sddefault.jpg`;
                        }
                      }}
                    />
                    <button
                      onClick={() => setIsPlaying(true)}
                      className="absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/30 transition-colors group"
                      aria-label="Reproducir video"
                    >
                      <div className="w-16 h-16 flex items-center justify-center bg-liec-primary-500 text-white group-hover:scale-110 transition-transform">
                        <Play className="w-7 h-7 ml-1" />
                      </div>
                    </button>
                  </>
                )}
              </div>

              {/* Video Info */}
              <div className="mt-4 space-y-2">
                <div className="flex items-start justify-between gap-4">
                  <h2 className="text-lg font-semibold text-liec-neutral-700 line-clamp-2">
                    {selectedVideo.title}
                  </h2>
                  <button
                    onClick={() => setSelectedVideo(null)}
                    className="flex-shrink-0 p-1 text-liec-neutral-400 hover:text-liec-neutral-600 transition-colors"
                    aria-label="Volver a la biblioteca"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="flex items-center gap-4 text-sm text-liec-neutral-500">
                  <span>{selectedVideo.instructor ?? 'LIEC'}</span>
                  <span className="flex items-center gap-1">
                    <Eye className="w-3.5 h-3.5" />
                    {selectedVideo.viewCount.toLocaleString('es-MX')} vistas
                  </span>
                </div>
                <p className="text-sm text-liec-neutral-600 line-clamp-3">
                  {selectedVideo.description.length > 200
                    ? selectedVideo.description.slice(0, 200) + '...'
                    : selectedVideo.description}
                </p>
              </div>
            </motion.div>

            {/* Sidebar Video List */}
            <motion.div
              className="lg:w-80 xl:w-96"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, ease: 'easeOut', delay: 0.1 }}
            >
              <p className="text-xs font-medium text-liec-neutral-500 uppercase tracking-wider mb-3">
                Más videos
              </p>
              <div className="space-y-1 max-h-[calc(100vh-320px)] overflow-y-auto scrollbar-hide">
                {sidebarVideos.map((video) => (
                  <SidebarVideoCard
                    key={video.id}
                    video={video}
                    onClick={() => setSelectedVideo(video)}
                  />
                ))}
                {sidebarVideos.length === 0 && (
                  <p className="text-sm text-liec-neutral-400 py-4">No hay más videos en esta categoría</p>
                )}
              </div>
            </motion.div>
          </div>
        ) : (
          /* Grid Library View */
          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            {filteredVideos.map((video, index) => (
              <VideoGridCard
                key={video.id}
                video={video}
                index={index}
                onClick={() => setSelectedVideo(video)}
              />
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
}

/* ─── Sub-components ─────────────────────────────────────────────────────── */

function VideoGridCard({ video, index, onClick }: { video: VideoItem; index: number; onClick: () => void }) {
  return (
    <motion.button
      onClick={onClick}
      className="w-full text-left group focus:outline-none focus:ring-2 focus:ring-liec-primary-500"
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.04, ease: 'easeOut' }}
    >
      {/* Thumbnail */}
      <div className="relative w-full aspect-video bg-gray-100 overflow-hidden">
        <img
          src={video.thumbnailUrl}
          alt={video.title}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />
        <div className="absolute bottom-2 right-2 flex items-center gap-1 bg-black/70 text-white text-xs px-1.5 py-0.5">
          <Eye className="w-3 h-3" />
          {video.viewCount.toLocaleString('es-MX')}
        </div>
      </div>

      {/* Info */}
      <div className="mt-2 space-y-1">
        <h3 className="text-sm font-medium text-liec-neutral-700 line-clamp-2 group-hover:text-liec-primary-500 transition-colors">
          {video.title}
        </h3>
        <p className="text-xs text-liec-neutral-500">
          {video.instructor ?? 'LIEC'}
        </p>
      </div>
    </motion.button>
  );
}

function SidebarVideoCard({ video, onClick }: { video: VideoItem; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="flex gap-3 p-2 w-full text-left hover:bg-gray-50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-liec-primary-500"
    >
      <img
        src={video.thumbnailUrl}
        alt={video.title}
        loading="lazy"
        className="w-28 h-16 object-cover flex-shrink-0"
      />
      <div className="flex-1 min-w-0">
        <h4 className="text-xs font-medium text-liec-neutral-700 line-clamp-2">
          {video.title}
        </h4>
        <p className="text-xs text-liec-neutral-500 mt-0.5">
          {video.instructor ?? 'LIEC'}
        </p>
        <div className="flex items-center gap-1 mt-0.5 text-xs text-liec-neutral-400">
          <Eye className="w-3 h-3" />
          <span>{video.viewCount.toLocaleString('es-MX')}</span>
        </div>
      </div>
    </button>
  );
}

function VideoGridSkeleton() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="animate-pulse">
          <div className="w-full aspect-video bg-gray-200" />
          <div className="mt-2 space-y-1.5">
            <div className="h-4 bg-gray-200 w-full" />
            <div className="h-4 bg-gray-200 w-3/4" />
            <div className="h-3 bg-gray-200 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}
